'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Price from '@/components/price/Price';
import { useModal } from '@/components/modal/ModalProvider';
import { findBiggestDiscount } from '@/utils/discounts';
import { resolveImageAltText } from '@/lib/utils';
import { getDisplayPriceData, formatPriceLabel } from '@/utils/variants';

export default function ProductCard({
  cardData,
  index: cardIndex,
  button_text,
  campaign,
  collectionPage
}) {
  const {
    card_title,
    description,
    expanded_card,
    superlative,
    superlative_feature_styling,
    card_type,
    extendedData
  } = cardData ?? {};
  const products = extendedData?.products;
  const productCardData = card_type?.[0]?.product;
  const preselectedIndex = getPreselectedIndex(productCardData, products);
  const [selected, setSelected] = useState(preselectedIndex);
  const { showPriceDetails } = useModal();

  if (!productsAreValid(products)) {
    console.warn('[ProductCard] No valid products found', card_title);
    return null;
  }

  function handleClick(index) {
    setSelected(index);
  }

  function handlePriceModalClick(e, variant) {
    e.preventDefault();
    showPriceDetails(
      `product-card-${variant}`,
      null,
      campaign?.pricing_details
    );
  }

  return (
    <div
      id={`product-group-card-${cardData?.uid}`}
      className={`border-dawn-grey-30 border bg-white ${expanded_card ? 'lg:col-span-3' : 'col-span-1'} flex`}
      data-optimizely="product-group-card"
    >
      {expanded_card &&
        products.map((product, index) => {
          const displayPriceData = getDisplayPriceData(
            product?.shopifyProduct?.variants || [],
            product?.productSizeDisplay
          );
          const productUrl =
            displayPriceData.displayFormat === 'specific' &&
            displayPriceData.variantName
              ? `${product.url}?size=${displayPriceData.variantName.replace(/\s+/g, '+')}`
              : product.url;
          return (
            <Link
              key={index}
              href={productUrl}
              prefetch={false}
              title={product.shopifyProduct.title}
              className={index == selected ? 'visible' : 'hidden'}
            >
              <Image
                src={product.image.url}
                alt={resolveImageAltText(product.image)}
                width="744"
                height="365"
                priority={
                  index == 0 && collectionPage && cardIndex <= 2 ? true : false
                }
                sizes="(min-width: 1024px) 744px, (min-width: 640px) 450px, 597px"
                className="hidden lg:block"
              />
            </Link>
          );
        })}
      <div className="flex max-w-full flex-col justify-between">
        <div
          className={`flex flex-col px-6 pt-5 ${expanded_card ? 'lg:h-full lg:justify-center lg:px-10' : ''}`}
        >
          {products.map((product, index) => {
            const displayPriceData = getDisplayPriceData(
              product?.shopifyProduct?.variants || [],
              product?.productSizeDisplay
            );
            const productUrl =
              displayPriceData.displayFormat === 'specific' &&
              displayPriceData.variantName
                ? `${product.url}?size=${displayPriceData.variantName.replace(/\s+/g, '+')}`
                : product.url || '#';
            return (
              <Link
                key={index}
                href={productUrl}
                prefetch={false}
                title={product.title}
                className={`flex flex-wrap ${index === selected ? 'visible' : 'hidden'}`}
                data-cy={product.title}
              >
                <h3 className="h3 nowrap mr-3">
                  {index === selected ? card_title : product.title}
                </h3>
                {superlative && (
                  <div
                    className={`${superlative_feature_styling ? 'bg-fiery-cognac-110 text-cozy-cognac-10' : 'bg-warm-pewter-80 text-burnt-cognac-130'} badge-text-r nowrap self-center rounded-full px-3 py-[2px] text-center`}
                  >
                    {superlative}
                  </div>
                )}
              </Link>
            );
          })}

          {description && <p className="body-s pt-2">{description}</p>}

          {products.length === 1 && (
            <div className="flex pt-3 pb-5">
              {products.map((product, index) => {
                const displayPriceData = getDisplayPriceData(
                  product?.shopifyProduct?.variants || [],
                  product?.productSizeDisplay
                );
                const priceLabel = formatPriceLabel(
                  displayPriceData.displayFormat,
                  displayPriceData.variantName
                );
                const discountMsg = findBiggestDiscount(
                  product?.shopifyProduct?.variants || [],
                  campaign
                );
                return (
                  <div
                    key={index}
                    className={`flex ${index === selected ? 'visible' : 'hidden'}`}
                  >
                    <div className="price-r-label mr-2">{priceLabel}</div>
                    <div>
                      <Price
                        key={index}
                        price={displayPriceData?.price?.amount}
                        compareAtPrice={
                          displayPriceData?.compareAtPrice?.amount
                        }
                        priceClassOverride="price-r text-burnt-cognac"
                      />
                      <div
                        className="price-s-label text-grey-text cursor-pointer underline"
                        onClick={(e) =>
                          handlePriceModalClick(
                            e,
                            product.shopifyProduct?.handle
                          )
                        }
                      >
                        Price details
                      </div>
                    </div>

                    {discountMsg && typeof discountMsg === 'string' && (
                      <div className="badge-text-r border-burnt-cognac text-burnt-cognac mt-[2px] self-start rounded-full border px-2 py-px text-center">
                        {discountMsg}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="px-6">
            {products.length > 1 && (
              <>
                {products.map((product, index) => {
                  const displayPriceData = getDisplayPriceData(
                    product?.shopifyProduct?.variants || [],
                    product?.productSizeDisplay
                  );
                  const priceLabel = formatPriceLabel(
                    displayPriceData.displayFormat,
                    displayPriceData.variantName
                  );
                  const discountMsg = findBiggestDiscount(
                    product?.shopifyProduct?.variants,
                    campaign
                  );
                  return (
                    <div
                      key={index}
                      className={`flex pt-3 pb-5 ${index == selected ? 'visible' : 'hidden'}`}
                    >
                      <div className="price-r-label mr-2">{priceLabel}</div>
                      <div>
                        <Price
                          key={index}
                          price={displayPriceData?.price?.amount}
                          compareAtPrice={
                            displayPriceData?.compareAtPrice?.amount
                          }
                          priceClassOverride="price-r text-burnt-cognac"
                        />

                        <div
                          className="price-s-label text-grey-text cursor-pointer underline"
                          onClick={(e) =>
                            handlePriceModalClick(
                              e,
                              product.shopifyProduct?.handle
                            )
                          }
                        >
                          Price details
                        </div>
                      </div>
                      {discountMsg && typeof discountMsg === 'string' && (
                        <div className="badge-text-r border-burnt-cognac text-burnt-cognac mt-[2px] self-start rounded-full border px-2 py-px text-center">
                          {discountMsg}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="mb-5 flex w-full cursor-pointer justify-center">
                  {products.map((item, index) => {
                    return (
                      <button
                        type="button"
                        key={index}
                        className={`body-s border-dawn-grey-30 flex w-full border py-2 text-center ${index == products.length - 1 ? 'rounded-r-full border-l-0' : index == 0 ? 'rounded-l-full' : 'border-l-0'} ${selected == index ? 'bg-parlor-green text-white' : 'hover:bg-parlor-green-30 bg-white text-black hover:text-black'}`}
                        onClick={() => handleClick(index)}
                        aria-pressed={selected == index ? 'true' : 'false'}
                      >
                        <div className="mx-auto self-center px-1">
                          {item.title}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {products.map((product, index) => {
            const displayPriceData = getDisplayPriceData(
              product?.shopifyProduct?.variants || [],
              product?.productSizeDisplay
            );
            const productUrl =
              displayPriceData.displayFormat === 'specific' &&
              displayPriceData.variantName
                ? `${product.url}?size=${displayPriceData.variantName.replace(/\s+/g, '+')}`
                : product.url;
            return (
              <Link
                key={index}
                href={productUrl}
                prefetch={false}
                title={product.shopifyProduct.title}
                className={index == selected ? 'visible' : 'hidden'}
                data-cy="product-group-card-link"
              >
                <div className="max-h-[310px]">
                  <Image
                    src={product.image.url}
                    alt={resolveImageAltText(product.image)}
                    width="597"
                    height="334"
                    sizes="(min-width: 1024px) 371px, (min-width: 640px) 450px, 597px"
                    priority={index == 0 && collectionPage && cardIndex <= 2}
                    className={`${expanded_card ? 'block lg:hidden' : ''}`}
                  />
                </div>
                <button
                  className="btn btn-primary w-full"
                  aria-label={
                    button_text + ' for ' + product.shopifyProduct.title
                  }
                  data-optimizely="product-group-card-cta"
                >
                  {button_text}
                </button>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Simple validation specific to this component.
 */
function productsAreValid(products) {
  if (!Array.isArray(products)) {
    return false;
  }
  for (const product of products) {
    if (!product?.shopifyProduct || !product?.image?.url || !product?.url) {
      return false;
    }
  }
  return true;
}

function getPreselectedIndex(productCardData, products) {
  let preselectedIndex = 0;
  if (productCardData?.product?.preselect && products?.length > 0) {
    preselectedIndex = Math.min(
      productCardData.product.preselect - 1,
      products.length - 1
    );
  }
  return preselectedIndex;
}
